import { z } from 'zod';

import { DocumentStatus, ReportStatus } from '../enums';
import { PaginationSchema } from './pagination';

/**
 * The request contracts of `uml/18-api-specification.md` §9.
 *
 * They live in `shared` and not in the API because the web app parses the same
 * shapes before it sends them, so a field renamed here breaks both builds in
 * one commit.
 *
 * **These validate SHAPE only.** None of them knows whether a folder exists,
 * whether a document is still PENDING (RG134), or whether a move would create a cycle (RG152). Those are rules,
 * they live in the services, and a schema that started enforcing them would be
 * a second place to look - and the two copies would drift.
 */

/** `folders.name` is VARCHAR(255). The trim is what the row stores, RG158. */
const FolderName = z.string().trim().min(1, 'A folder needs a name').max(255);

/** Every identifier in this system is a SERIAL - doc 18 §2. */
const Id = z.coerce.number().int().positive();

/** `POST /folders`. RG118, RG125. */
export const CreateFolderSchema = z.object({
  name: FolderName,
  /** Absent or null: a folder at the root of the tree. */
  parentId: Id.nullable().optional(),
});
export type CreateFolderInput = z.infer<typeof CreateFolderSchema>;

/**
 * `PATCH /folders/:id`. RG118, RG152.
 *
 * `parentId: null` means "move to the root" and absent means "do not move" -
 * two different requests, which is why `.nullable().optional()` and not one or
 * the other.
 */
export const UpdateFolderSchema = z
  .object({
    name: FolderName.optional(),
    parentId: Id.nullable().optional(),
  })
  .refine((data) => data.name !== undefined || data.parentId !== undefined, {
    message: 'An update must rename or move the folder',
    path: ['name'],
  });
export type UpdateFolderInput = z.infer<typeof UpdateFolderSchema>;

/**
 * `GET /folders`. RG85, RG119 - one level, unpaged. Absent: the top of the
 * tree. There is no "null" in a query string, so absent is the only way to say
 * it.
 */
export const FolderListQuerySchema = z.object({
  parentId: Id.optional(),
});
export type FolderListQueryInput = z.infer<typeof FolderListQuerySchema>;

/**
 * The JSON `payload` field of `POST /documents`, doc 18 §2.7 - the file itself
 * is the multipart `file` field and is checked by the upload policy, not here.
 *
 * `z.coerce` throughout: a multipart payload carries numbers as text.
 *
 * D-057: no type, teacher or session - the folder classifies the document. An
 * old client that still sends them is not refused: `z.object` strips unknown keys.
 */
export const SubmitDocumentSchema = z.object({
  title: z.string().trim().min(1).max(255),
  /** RG83. A year a document could plausibly carry, not "any integer". */
  year: z.coerce.number().int().min(1900).max(2100),
  /** RG123. The student chooses; RG124 lets the manager change it. */
  suggestedFolderId: Id.optional(),
});
export type SubmitDocumentInput = z.infer<typeof SubmitDocumentSchema>;

/** `POST /documents/direct`. RG135 - published at once, into a folder chosen now. */
export const DirectUploadSchema = SubmitDocumentSchema.omit({ suggestedFolderId: true }).extend({
  folderId: Id,
});
export type DirectUploadInput = z.infer<typeof DirectUploadSchema>;

/** `POST /documents/:id/approve`. RG124, RG136, D-038. */
export const ApproveDocumentSchema = z.object({
  /** Absent: file it where the author suggested. */
  folderId: Id.optional(),
});
export type ApproveDocumentInput = z.infer<typeof ApproveDocumentSchema>;

/** `POST /documents/:id/reject`. RG90, RG137 - the reason is part of the record. */
export const RejectDocumentSchema = z.object({
  reason: z.string().trim().min(10, 'Say why, in a sentence the author can act on').max(1000),
});
export type RejectDocumentInput = z.infer<typeof RejectDocumentSchema>;

/** `GET /documents`. RG92, RG136 - published only, whatever the filters say. */
export const DocumentListQuerySchema = PaginationSchema.extend({
  /** Matches the title and the folder path, D-031. */
  q: z.string().trim().max(100).optional(),
  folderId: Id.optional(),
  year: z.coerce.number().int().min(1900).max(2100).optional(),
});
export type DocumentListQueryInput = z.infer<typeof DocumentListQuerySchema>;

/** `GET /me/documents`, D-037. The author's own proposals, whatever their state. */
export const MyDocumentsQuerySchema = PaginationSchema.extend({
  status: z.nativeEnum(DocumentStatus).optional(),
});
export type MyDocumentsQueryInput = z.infer<typeof MyDocumentsQuerySchema>;

/** `POST /documents/:id/ratings`. RG112, D-036 - an upsert, 201 or 200. */
export const RateDocumentSchema = z.object({
  score: z.coerce.number().int().min(1).max(5),
});
export type RateDocumentInput = z.infer<typeof RateDocumentSchema>;

/** `POST /documents/:id/comments`. RG113. */
export const CommentDocumentSchema = z.object({
  body: z.string().trim().min(1).max(2000),
});
export type CommentDocumentInput = z.infer<typeof CommentDocumentSchema>;

/** `POST /documents/:id/reports`. RG114 - a reason, because a manager must triage it. */
export const ReportDocumentSchema = z.object({
  reason: z.string().trim().min(10, 'Say what is wrong with it').max(1000),
});
export type ReportDocumentInput = z.infer<typeof ReportDocumentSchema>;

/** `POST /reports/:id/handle`. RG117 - dismiss it, or delete the document. */
export const HandleReportSchema = z.object({
  decision: z.enum(['DISMISS', 'DELETE_DOCUMENT']),
});
export type HandleReportInput = z.infer<typeof HandleReportSchema>;

/** `GET /reports`. RG115, RG117. */
export const ReportListQuerySchema = PaginationSchema.extend({
  status: z.nativeEnum(ReportStatus).optional(),
});
export type ReportListQueryInput = z.infer<typeof ReportListQuerySchema>;
